import template from 'modal/user/login.j2';

import { checkForm } from '../utils/errorHandling';

import { handleUtagEvent } from '../actions/initialActions';
import { replaceModalTransition } from '../actions/modalActions';
import {
    clearLoginData,
    loginData,
    loginFailure,
    loginCanceled,
    loginFinished,
    requestUserAddresses
} from '../actions/userActions';

import { init as forgotPasswordInit } from './forgotPasswordComponent';

import {
    userLoginSaga,
    loyaltyModalSaga,
    handleLoyaltyEventsSaga
} from '../sagas/userSagas';
import { getPromoBannersSaga } from '../sagas/promoSagas';

import {
    LOGIN_METHOD_SIGNIN,
    PRIMARY_HEADER_PROMO,
    ALL_PROMO_TYPES
} from '../constants';

import { modalShepherdSingleton } from '../../../core/js/components/modalShepherdComponent';
import { getDefaultOptions } from '../../../core/js/utils/a15componentUtils';
import { bindOnPopStateEvent } from '../../../core/js/utils/browserUtils';
import { generate } from '../../../core/js/utils/componentUtils';
import { debug, error } from '../../../core/js/utils/logger';
import { closeModal } from '../../../core/js/utils/modalUtils';
import { mDom, mDomMaintainInputVals } from '../../../core/js/utils/renderUtils';
import { removeHash } from '../../../core/js/utils/routerUtils';

import Cookies from '../services/cookies';

import { launchAsyncCreateUserModal } from '@core/js/utils/asyncLaunchers';

export function init(store, overrides = {}) {
    const config = {
        attachEvents: attachEvents,
        firstRender: firstRender,
        watch: 'core.modal.login',
        template: template,
        name: 'modal.login',
        replace: 'once',
        afterCancelAction: afterCancelAction,
        afterCloseAction: afterCloseAction,
        finalCloseAction: finalCloseAction,
        renderType: mDom,
        renderOptions: mDomMaintainInputVals,
        loader: {}
    };

    if (!_.isEmpty(overrides.userAction)) {
        // Login modal has been fired from an action, like Write Review
        //  or Add to Wishlist
        // Need to fire off an analytics event so that these actions can be tracked
        fireAnalytics(store, overrides.userAction);
    }

    return generate(config, store, overrides, getDefaultOptions(store));
}

/**
 * Bootstrap the component onto the page
 * @returns {undefined}
 */
export function initialize(store) {
    attachEvents(store);
}

function firstRender(store) {
    this.render(store, this);
    attachParsley(this, store);
}

/**
 * After close action for the modal shepard
 */
function afterCancelAction(store) {
    store.dispatch(loginCanceled());
}

/**
 * After close action for the modal shepard
 */
function afterCloseAction(store) {
    this.dispatch(clearLoginData());
    // Remove hash and push state on close
    removeHash();
    this.finalCloseAction(this);
}

/**
 * Whoever pops this modal can override this to do addition actions
 */
function finalCloseAction(component) {
}

/**
 * Attach component events to DOM
 * @param  {Object} store Redux store
 * @returns {undefined}
 */
function attachEvents(store) {
    const config = this;

    $(document).on(this.scopedEvent('submit'), this.scopedSelector('.js-login-form'), (e) => {
        e.preventDefault();
        debug('#login-form submit');
        this.attachLoader(store);

        const formData = {
            emailAddress: $(this.scopedSelector('[name="email"]')).val(),
            password: $(this.scopedSelector('[name="password"]')).val()
        };

        const payload = {
            formData: formData,
            method: LOGIN_METHOD_SIGNIN
        };

        let res = store.runSaga(userLoginSaga, payload).done.then((tokenSession) => {
            const state = store.getState();
            const disableLoyaltyModal = _.get(state, 'core.loyaltyForm.disableModal', false);
            this.removeLoader(store);

            modalShepherdSingleton.outcome = {
                loginSuccessful: true
            };

            if (!disableLoyaltyModal) {
                store.runSaga(loyaltyModalSaga, e, this.name);
            } else {
                this.closeAction(store);
            }

            /**
             * Testing Scenario
             * Integration tests will set cookie to true
             * When cookie is true, fetch all promo types instead of the one passed in
             */
            const promoTypes = Cookies.getCookieByName('urbn_testing_banners')
                ? ALL_PROMO_TYPES : [PRIMARY_HEADER_PROMO];

            // get updated primary header promo after login
            store.runSaga(getPromoBannersSaga, promoTypes);

            store.dispatch(requestUserAddresses());
            store.dispatch(loginFinished(tokenSession.userAuthToken));
            store.runSaga(handleLoyaltyEventsSaga, state);
        }).catch(err => {
            $(this.scopedSelector('[name="password"]')).val("");
            this.removeLoader(store);
            error(`Login failure ${JSON.stringify(err)}`);
            store.dispatch(loginFailure(err));
        });
    });

    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-user-create'), (e) => {
        e.preventDefault();
        launchAsyncCreateUserModal(e, config.name);
    });

    $(document).on(this.scopedEvent('touchend click'), this.scopedSelector('.js-user-forgot-password'), (e) => {
        e.preventDefault();

        const payload = replaceModalTransition(forgotPasswordInit, {}, e, config.name);
        $.event.trigger({
            type: payload.type,
            payload: payload
        });
    });

    // Close modal on back-button
    bindOnPopStateEvent(this, function(e) {
        closeModal(this, e);
    });
}

function attachParsley(component, store) {
    debug('modal: attachParsleyForLogin');

    $(component.scopedSelector('.js-login-form')).parsley().on('field:validated', (e) => {
        const [values, msgs] = checkForm(component.scopedSelector('.js-login-form'));

        let previousMsgs = {};
        if (store.getState().core.modal.login) {
            previousMsgs = store.getState().core.modal.login.messages;
        }
        store.dispatch(loginData(values, msgs, previousMsgs));
    });
}

function fireAnalytics(store, userAction) {
    const analyticsContainer = _.get(store.getState(), 'core.localizations.containers.analyticsContainer', {});

    const utagData = {
        page_type: analyticsContainer.account_login_modal_page_type,
        page_id: analyticsContainer.account_login_modal_page_id,
        category_id: analyticsContainer.account_login_modal_category_id,
        page_name: analyticsContainer.account_login_modal_page_name,
        page_action: userAction
    };

    store.dispatch(handleUtagEvent(utagData, 'view'));
}
